import { Button, Group, Link } from '@apollo/orbit';
import React, { useState } from 'react';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';

import { GithubSignupCTA } from 'src/app/onboarding/components';
import * as routes from 'src/app/onboarding/routes';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ampli } from 'src/lib/analytics/amplitude/vendor';
import Config from 'src/lib/config';

interface Props
  extends Pick<
    React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLDivElement>,
      HTMLDivElement
    >,
    'className'
  > {
  /**
   * URL to return to when a user has authenticated.
   *
   * This can be relative or absolute and we'll figure out what to do with it
   * depending on if we're using GitHub or a local login method.
   */
  callbackURL?: string;
  description: React.ReactNode;
  /**
   * Destination to send the user to when they click the sign up with email
   * button.
   */
  emailTo: React.ComponentProps<typeof Link>['to'];
  title: React.ReactNode;
}

/**
 * Take a relative or absolute URL and return a relative URL
 */
function relativeURL(href: string) {
  const url = new URL(href, Config.absoluteUrl);

  return `${url.pathname}${url.search}`;
}

export const CreateAccountCTA = ({
  className,
  description,
  emailTo,
  callbackURL,
  title,
}: Props) => {
  const [loggingInGithub, setLoggingInGithub] = useState<boolean>(false);

  const location = useLocation<{ from?: string }>();

  const { type } = useRouteParams(routes.signup);

  return (
    <div className={className}>
      <h2 className="mb-2 text-3xl font-semibold">{title}</h2>
      <p className="mb-8 text-lg">{description}</p>
      <Group className="flex-col lg:flex-row" gap={4}>
        <GithubSignupCTA
          className="w-full lg:w-1/2"
          loggingIn={loggingInGithub}
          setLoggingIn={setLoggingInGithub}
          callbackUrl={routes.welcome.pathFrom({
            location,
            fromRouteConfig: routes.signup,
          })}
          type={type}
        />
        <Button
          className="w-full lg:w-1/2"
          isDisabled={loggingInGithub}
          as={ReactRouterLink}
          to={emailTo}
          variant="secondary"
          size="lg"
          data-testid="email-signup"
          onClick={() => {
            ampli.registrationAuthMethodSelected({
              RegistrationVariant: 'studio-direct',
              AuthorizationMethod: 'email',
            });
          }}
        >
          Sign up with email
        </Button>
      </Group>
      <div className="mb-8 mt-4 text-center text-base">
        Enterprise customers,{' '}
        <Button
          variant="buttonLink"
          isDisabled={loggingInGithub}
          as={ReactRouterLink}
          to={routes.login.locationFrom({
            location,
            fromRouteConfig: routes.signup,
            patch: {
              from: callbackURL && relativeURL(callbackURL),
              type: 'sso',
            },
          })}
        >
          sign in with Enterprise SSO
        </Button>
        .
      </div>
      <div className="text-center text-sm text-secondary">
        Already have an account?{' '}
        <Link
          to={routes.login.locationFrom({
            location,
            fromRouteConfig: routes.signup,
            patch: {
              from: callbackURL && relativeURL(callbackURL),
            },
          })}
        >
          Sign in
        </Link>
        .
      </div>
    </div>
  );
};
